import classNames from 'classnames';
import React, { useMemo } from 'react';
import {
	IChatMediaMessage,
	IChatVoiceMessage,
	isMediaMessage,
	IMetaResource,
	isVoiceMessage,
} from '@enuan/chat__utils';
import { useMessageContext } from '../../message/message.context';
import { useBlockMessageClassNames } from 'src/hooks/core/messages.hooks';
import { Message } from '../../UI/Message/Message';
import { ImageBalloonContent } from './components/ImageBalloonContent';
import { AudioBalloonContent } from './components/AudioBalloonContent';
import { DocBalloonContent } from './components/DocBalloonContent';
import { styled } from '@mui/material/styles';
import type { IOptionalMessageProps } from '../../UI/Message/Message';

type IMediaMessageContent =
	| IChatMediaMessage<IMetaResource>
	| IChatVoiceMessage<IMetaResource>;

export const MediaMessage = styled(
	React.forwardRef<HTMLElement, { className?: string } & IOptionalMessageProps>(
		function MediaMessage({ className, ...props }, ref) {
			const { message } = useMessageContext<IMediaMessageContent>();
			const messageClassName = useBlockMessageClassNames();

			const isMedia = isMediaMessage(message);
			const isImage = isMedia && message._data.mime_type?.includes('image/');
			const isAudio = isMedia && message._data.mime_type?.includes('audio/');
			const isVoice = isVoiceMessage(message);
			const isDoc = isMedia && !isImage && !isAudio && !isVoice;
			const { _data } = message;

			const content = useMemo(() => {
				if (isImage)
					return (
						<ImageBalloonContent data={_data} timestamp={message.timestamp} />
					);
				if (isAudio || isVoice)
					return <AudioBalloonContent data={_data} isVoice={isVoice} />;
				return <DocBalloonContent data={_data} />;
			}, [isAudio, isImage, isVoice, _data, message.timestamp]);

			const showFooter = !isImage;

			return (
				<Message
					{...props}
					ref={ref}
					message={message}
					className={classNames(
						className,
						messageClassName,
						'message--media',
						isDoc && 'message--document',
						isImage && 'message--image',
						isAudio && 'message--audio',
						isVoice && 'message--voice',
					)}
					customFooter={showFooter ? undefined : null}
				>
					{content}
				</Message>
			);
		},
	),
	{
		label: 'MediaMessage',
		target: 'MediaMessage-root',
	},
)({
	'&.message--media .message__download': {
		flexShrink: 0,
		justifySelf: 'flex-end',
		marginLeft: 'auto',
		display: 'flex',
		'& .line': {
			strokeLinecap: 'round',
			strokeLinejoin: 'round',
			strokeWidth: 1,
			stroke: 'var(--message_color)',
		},
	},
	'&.message--audio, &.message--voice': {
		width: '100%',
		maxWidth: 'min(calc(100% - (20px * 2)), 50em)',
		'& .Message-balloon': {
			width: '100%',
		},
		'&.message--left': {
			alignSelf: 'flex-start',
		},
		'&.message--right': {
			alignSelf: 'flex-end',
		},
	},
	'&.message--image .Message-balloon': {
		padding: '0.5em',
		'& .balloon__frame': {
			position: 'relative',
			overflow: 'hidden',
		},
		'& .message__image': {
			borderRadius: '0.6em',
			minHeight: '5em',
			minWidth: '10em',
			margin: 0,
			maxWidth: '100%',
			overflow: 'hidden',
			'& img': {
				display: 'block',
				maxWidth: '100%',
				height: 'auto',
			},
		},
		'& .message__time': {
			position: 'absolute',
			bottom: 0,
			right: 0,
			borderTopLeftRadius: '100%',
			paddingTop: '0.6em',
			paddingLeft: '0.8em',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'initial',
			maxWidth: '100%',
		},
		'& .details': {
			pointerEvents: 'all',
			fontSize: '1em',
			borderBottomLeftRadius: '2em',
			display: 'flex',
			alignItems: 'center',
			position: 'absolute',
			width: 'calc(100% + 2.5em)',
			top: 0,
			left: 'calc(100% - 2.5em)',
			transition: 'all 1s ease',
			'&.details--open': {
				left: '-2.5em',
			},
			'& > *': {
				margin: '0 0.2em',
			},
			'& button.close, & button.open': {
				padding: '0.5em 1em',
				margin: 0,
			},
			'& button.open': {
				paddingLeft: '1.5em',
			},
			'& button.close': {
				marginLeft: 'auto',
				'& .icon': {
					width: '1.2em',
					height: 'auto',
					stroke: 'none',
				},
			},
			'& .message__download .line': {
				strokeWidth: 3,
			},
		},
	},
	'&.message--image .message__time, &.message--image .details': {
		gap: '0.5em',
		backgroundColor: 'var(--message__image__details_background)',
		'& .message__details': {
			flexShrink: 1,
			flexGrow: 1,
			overflow: 'hidden',
			fontSize: '0.8em',
			'& .details__main': {
				alignItems: 'center',
				flexDirection: 'row',
				flexWrap: 'wrap',
				columnGap: '0.5em',
			},
			'& .details__secondary': {
				flexWrap: 'nowrap',
			},
		},
	},
	'&.message--image .message__image': {
		backgroundColor: 'var(--message__image_background)',
	},
	'&.message--image.message--right .message__time, &.message--image.message--right .details':
		{
			backgroundColor: 'var(--message--right__image__details_background)',
		},
	'&.message--image.message--right .message__image': {
		backgroundColor: 'var(--message--right__image_background)',
	},
});
